/* eslint-disable no-nested-ternary */
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "components/atoms/buttons/Button";
import { ArrowRight } from "components/atoms/Icon";
import { useRouter } from "components/hooks";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { handleSetErrorAndSwitchFormState, uploadFileToS3 } from "utils/HelperFunctions";
import { patch, post, remove } from "utils/request";
import { getAirDropMementoRoute, getMementoInfo, publishMemento } from "apis/magnets";
import { useLoaderContext } from "components/context/LoaderContext";
import FormLayoutWithModal from "components/organisms/activations/FormLayoutWithModal";
import { useDataProviderContext } from "pages/MagnetDetails/DataProvider";
import MerchDropBrandPreviewModalContainer from "components/organisms/merchdrop/MerchDropBrandPreviewModalContainer";
import BrandClaimPreviewModalContainer from "components/organisms/claimcode/BrandClaimPreviewModalContainer";
import PreviewMementoModal from "./PreviewMementoModal";
import { GLOBAL_LOADER_TEXT, TOAST_CONFIG } from "../../constants";
import { CreateMementoSchema, getDefaultValues, getPaylaodValues } from "./ValidationSchema";
import MementoForm from "./MementoForm";

type Props = {
  magnet: BrandMagnetDetails;
  mementoData?: Memento;
  revalidateAirdropDetails: Function;
  activitionKey: ActivationsKeys;
};

const formFields = {
  name: "0",
  media: "0",
  start_time: "0",
  description: "0",
};

export default function CreateAirDropMemento({ magnet, mementoData, revalidateAirdropDetails, activitionKey }: Props) {
  const router = useRouter();
  const [isMementoPreviewVisible, setIsMementoPreviewVisible] = useState(false);
  const { loading, setLoading } = useLoaderContext();
  const { magnetMementos } = useDataProviderContext();

  const onThumbnailUploadProgress = (e: ProgressEvent) => {
    setLoading(true, `Uploading Memento thumbnail... ${((e.loaded * 100) / e.total).toFixed(2)}%`);
  };

  const onMediaUploadProgress = (e: ProgressEvent) => {
    setLoading(true, `Uploading Memento media... ${((e.loaded * 100) / e.total).toFixed(2)}%`);
  };

  const formMethods = useForm<MementoFormType>({
    defaultValues: getDefaultValues(mementoData, activitionKey, magnetMementos),
    // @ts-ignore
    resolver: yupResolver(CreateMementoSchema[activitionKey]),
  });

  const {
    handleSubmit,
    setError,
    getValues,
    formState: { isDirty },
  } = formMethods;

  const uploadFile = async (file: File, uploadingWhat: "media" | "thumbnail", onUploadProgress: any) => {
    try {
      const response = await uploadFileToS3(file, new AbortController().signal, onUploadProgress);
      if (response?.file_url) {
        return response.file_url;
      }
      throw new Error(`Failed to upload memento ${uploadingWhat}`);
    } catch (e: any) {
      setError(uploadingWhat, { message: e.message });
      throw new Error(`Failed to upload memento ${uploadingWhat}`);
    }
  };

  const handleSaveMementoAsDraftSuccess = (data: any, redirect: boolean = true) => {
    toast.success("Saved Memento as draft.", TOAST_CONFIG);
    setLoading(false);
    if (redirect) router.go(-1);
    return data;
  };
  const handleSaveMementoAsDraftError = (
    errorMessage: string,
    error: any,
    errorData: Record<string, any> | undefined,
  ) => {
    setLoading(false);
    toast.error(errorMessage, TOAST_CONFIG);
    // @ts-ignore
    handleSetErrorAndSwitchFormState<any>(errorData, formFields, setError);
  };

  const handleSaveMementoAsDraft = async (data: MementoFormType, redirectAfterCreate: boolean = true) => {
    try {
      if (data.media_url_new) {
        // eslint-disable-next-line no-param-reassign
        data.media_url = data.media_url_new;
      }
      if (data.thumbnail) {
        // eslint-disable-next-line no-param-reassign
        data.thumbnail_url = (await uploadFile(data.thumbnail, "thumbnail", onThumbnailUploadProgress)) as string;
      }
      if (data.media && activitionKey !== "claim_code") {
        // eslint-disable-next-line no-param-reassign
        data.media_url = (await uploadFile(data.media, "media", onMediaUploadProgress)) as string;
      }
      setLoading(true, GLOBAL_LOADER_TEXT.UPDATING_MEMENTO);

      return await post(getAirDropMementoRoute(magnet.id, activitionKey), getPaylaodValues(data, activitionKey), {
        headers: {
          "Content-Type": activitionKey === "claim_code" ? "multipart/form-data" : "application/json",
        },
        processData: (successData) => handleSaveMementoAsDraftSuccess(successData, redirectAfterCreate),
        processError: handleSaveMementoAsDraftError,
      });
    } catch (err) {
      toast.error(err?.toString(), TOAST_CONFIG);
      setLoading(false);
      return null;
    }
  };

  const handleUpdateMementoSuccess = (redirect: boolean = true) => {
    revalidateAirdropDetails();
    toast.success("Memento Update Success", TOAST_CONFIG);
    setLoading(false);
    if (redirect) router.go(-1);
  };

  const handleUpdateMemento = async (data: MementoFormType, redirectAfterUpdate: boolean = true) => {
    try {
      if (data.media_url_new) {
        // eslint-disable-next-line no-param-reassign
        data.media_url = data.media_url_new;
      }
      if (data.thumbnail) {
        // eslint-disable-next-line no-param-reassign
        data.thumbnail_url = (await uploadFile(data.thumbnail, "thumbnail", onThumbnailUploadProgress)) as string;
      }
      if (data.media) {
        // eslint-disable-next-line no-param-reassign
        data.media_url = (await uploadFile(data.media, "media", onMediaUploadProgress)) as string;
      }
      setLoading(true, GLOBAL_LOADER_TEXT.UPDATING_MEMENTO);
      return await patch(
        getMementoInfo(magnet.id, activitionKey, mementoData?.id as string),
        getPaylaodValues(data, activitionKey),
        {
          processData: () => handleUpdateMementoSuccess(redirectAfterUpdate),
          processError: handleSaveMementoAsDraftError,
        },
      );
    } catch (err) {
      toast.error(err?.toString(), TOAST_CONFIG);
      setLoading(false);
      return null;
    }
  };

  const handlePreviewMemento = () => {
    setIsMementoPreviewVisible(true);
  };

  const handleCloseMementoPreviewModal = () => {
    setIsMementoPreviewVisible(false);
  };

  const handlePublishMemento = async () => {
    // Edit form
    const data = getValues();
    let mementoId;
    if (mementoData) {
      mementoId = mementoData.id;
      // @ts-ignore
      await handleUpdateMemento(data, false);
    } else {
      // @ts-ignore
      const { data: createdMemento } = await handleSaveMementoAsDraft(data, false);
      mementoId = createdMemento.id;
    }

    setLoading(true, GLOBAL_LOADER_TEXT.PUBLISHING_MEMENTO);

    post(
      publishMemento(magnet.id, mementoId),
      {},
      {
        processData: () => {
          toast.info("Airdrop Scheduled Successfully.", TOAST_CONFIG);
          router.go(-1);
          setLoading(false);
        },
        processError: () => {
          toast.error("Failed to Schedule Airdrop. Please try after some time", TOAST_CONFIG);
          setLoading(false);
        },
      },
    );
  };

  const handleDeleteMemento = async () => {
    try {
      // TODO: Pass a prop for data
      if (!mementoData?.id) {
        throw new Error("No Memento id was found.");
      }
      setLoading(true);
      remove(getMementoInfo(magnet.id, activitionKey, mementoData.id), {
        processData: () => {
          setLoading(false);
          toast.success("Deleted Memento", TOAST_CONFIG);
          router.go(-1);
        },
        processError: (errorMessage) => {
          setLoading(false);
          toast.error(errorMessage, TOAST_CONFIG);
        },
      });
    } catch (err) {
      toast.error(err?.toString(), TOAST_CONFIG);
      setLoading(false);
    }
  };

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...formMethods}>
      {/* Preview Modal  */}
      {isMementoPreviewVisible &&
        (activitionKey === "merch_drop" ? (
          <MerchDropBrandPreviewModalContainer
            disabled={loading}
            magnetDetails={magnet}
            onClose={handleCloseMementoPreviewModal}
            handlePublishMerchDrop={handlePublishMemento}
            handleSubmit={handleSubmit}
          />
        ) : activitionKey === "claim_code" ? (
          <BrandClaimPreviewModalContainer
            disabled={loading}
            onModalClose={handleCloseMementoPreviewModal}
            handlePublishuttonClick={handleSubmit(handlePublishMemento)}
            magnetDetails={magnet}
          />
        ) : (
          <PreviewMementoModal
            disabled={loading}
            handlePublishMemento={handlePublishMemento}
            magnet={magnet}
            open={isMementoPreviewVisible}
            onClose={handleCloseMementoPreviewModal}
          />
        ))}
      <FormLayoutWithModal
        magnetImageUrl={magnet.image_url}
        magnetName={magnet.name}
        brandName={magnet.brand_name}
        activationType="airDrop"
        isDirty={isDirty}
      >
        <div className="mt-2 font-sora">
          <div>
            <p className="col-span-12 mb-6  font-satoshi text-xs font-semibold text-secondary">
              Disclaimer: Once the memento is published, this data can&apos;t be edited
            </p>

            {/* Memento Form */}
            <div>
              <MementoForm activationKey={activitionKey} />
            </div>
          </div>
        </div>
      </FormLayoutWithModal>

      <div className=" flex w-full justify-end space-x-4 px-24 py-3 shadow-2xl">
        {mementoData?.status === 0 && (
          <Button
            text="DELETE MEMENTO"
            variant="whiteOutline"
            additionalClasses="sm:px-8 sm:py-5  text-xs xl:text-sm 2xl:text-lg"
            // DELTE MOMEMTO
            onClick={handleDeleteMemento}
          />
        )}
        <Button
          variant="rustPinkSolid"
          text={mementoData?.status === 0 ? "UPDATE" : "SAVE FOR DRAFT"}
          additionalClasses="sm:px-8 text-xs xl:text-sm 2xl:text-lg"
          // @ts-ignore
          onClick={handleSubmit(mementoData ? handleUpdateMemento : handleSaveMementoAsDraft)}
        />

        <Button
          text="PREVIEW"
          additionalClasses="sm:px-8 sm:py-5  text-xs xl:text-sm 2xl:text-lg"
          onClick={handleSubmit(handlePreviewMemento)}
          icon={ArrowRight}
          variant="magnetiqSolid"
        />
      </div>
    </FormProvider>
  );
}
